import { darkTheme, legacyTheme } from "../styles/theme.css";

import { atom } from "jotai";

import type { AnimationLevel, ResolvedThemeName, ThemeName } from "../types";
import { PREFERENCE_DEFAULTS, appAtom } from "./app";

export const themeAtom = atom(
  (get) => get(appAtom).theme,
  (_get, set, newTheme: ThemeName) => {
    set(appAtom, (prev) => ({ ...prev, theme: newTheme }));
  },
);

const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export const prefersDarkAtom = atom(false);

prefersDarkAtom.onMount = (set) => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return;
  }

  const media = window.matchMedia(DARK_SCHEME_QUERY);

  set(media.matches);

  const handleChange = (event: MediaQueryListEvent) => set(event.matches);

  media.addEventListener("change", handleChange);

  return () => media.removeEventListener("change", handleChange);
};

export const resolvedThemeAtom = atom<ResolvedThemeName>((get) => {
  const theme = get(appAtom).theme;

  if (theme !== "system") {
    return theme;
  }

  return get(prefersDarkAtom) ? "dark" : "light";
});

export const themeClassAtom = atom((get) => {
  switch (get(resolvedThemeAtom)) {
    case "dark":
      return darkTheme;
    case "legacy":
      return legacyTheme;
    default:
      return null; // light = :root
  }
});

export const animationAtom = atom(
  (get) => get(appAtom).animation ?? PREFERENCE_DEFAULTS.animation,
  (_get, set, newAnimation: AnimationLevel) => {
    set(appAtom, (prev) => ({ ...prev, animation: newAnimation }));
  },
);

export const highContrastAtom = atom(
  (get) => get(appAtom).highContrast ?? PREFERENCE_DEFAULTS.highContrast,
  (_get, set, newHighContrast: boolean) => {
    set(appAtom, (prev) => ({ ...prev, highContrast: newHighContrast }));
  },
);

export const shortcutsEnabledAtom = atom(
  (get) => get(appAtom).shortcuts ?? PREFERENCE_DEFAULTS.shortcuts,
  (_get, set, newShortcuts: boolean) => {
    set(appAtom, (prev) => ({ ...prev, shortcuts: newShortcuts }));
  },
);

export const resetPreferencesAtom = atom(null, (_get, set) => {
  set(appAtom, (prev) => ({ ...prev, ...PREFERENCE_DEFAULTS }));
});

// A key absent from storage is a key still at its default
export const preferencesAreDefaultAtom = atom((get) => {
  const state = get(appAtom);

  return (
    (state.theme ?? PREFERENCE_DEFAULTS.theme) === PREFERENCE_DEFAULTS.theme &&
    (state.animation ?? PREFERENCE_DEFAULTS.animation) ===
      PREFERENCE_DEFAULTS.animation &&
    (state.timeZone ?? PREFERENCE_DEFAULTS.timeZone) ===
      PREFERENCE_DEFAULTS.timeZone &&
    (state.highContrast ?? PREFERENCE_DEFAULTS.highContrast) ===
      PREFERENCE_DEFAULTS.highContrast &&
    (state.shortcuts ?? PREFERENCE_DEFAULTS.shortcuts) ===
      PREFERENCE_DEFAULTS.shortcuts
  );
});
